/**
 * Adapted from PlayCanvas Engine's gsplat-resource.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type { GSplatData, PlyStorage } from '@/plugins/gsplat/core/GSplatData'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat, type GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import {
  GSplatResourceBase,
  type GSplatResourceBaseOptions,
  type GSplatResourceDataSource,
} from '@/plugins/gsplat/unified/resource/GSplatResourceBase'

const SH_C0 = 0.28209479177387814
const SH_COEFFICIENTS = Object.freeze([0, 3, 8, 15] as const)
const READ_GLSL = '#include "gsplatUncompressedVS"'
const READ_WGSL = '#include "gsplatUncompressedVS"'

type SplatShapeReader = NonNullable<
  GSplatResourceBaseOptions<GSplatData, unknown>['readSplatShape']
>
type SplatColorReader = NonNullable<
  GSplatResourceBaseOptions<GSplatData, unknown>['readSplatColor']
>

export type GSplatResourceCreateOptions = Omit<
  GSplatResourceBaseOptions<GSplatData, unknown>,
  'adapter' | 'data' | 'format'
>

interface PackedUncompressedData {
  readonly splatColor: Uint16Array
  readonly transformA: Uint32Array
  readonly transformB: Uint16Array
  readonly splatSH_1to3?: Uint32Array
  readonly splatSH_4to7?: Uint32Array
  readonly splatSH_8to11?: Uint32Array
  readonly splatSH_12to15?: Uint32Array
}

function createFormat(adapter: GSplatGraphicsAdapter, shBands: number): GSplatFormat {
  const streams: GSplatStreamDescriptor[] = [
    { name: 'splatColor', format: 'rgba16float' },
    { name: 'transformA', format: 'rgba32uint' },
    { name: 'transformB', format: 'rgba16float' },
  ]
  if (shBands > 0) {
    streams.push({ name: 'splatSH_1to3', format: 'rgba32uint' })
    if (shBands > 1) {
      streams.push({ name: 'splatSH_4to7', format: 'rgba32uint' })
      streams.push({
        name: 'splatSH_8to11',
        format: shBands > 2 ? 'rgba32uint' : 'r32uint',
      })
      if (shBands > 2) streams.push({ name: 'splatSH_12to15', format: 'rgba32uint' })
    }
  }
  return new GSplatFormat(adapter, streams, {
    readGLSL: READ_GLSL,
    readWGSL: READ_WGSL,
  })
}

function requireProp(data: GSplatData, name: string): PlyStorage {
  const property = data.getProp<PlyStorage>(name)
  if (!property) throw new Error(`GSplatResource: 缺少 property "${name}"`)
  if (property.length !== data.numSplats) {
    throw new Error(
      `GSplatResource: property "${name}" 长度不匹配，expected=${data.numSplats}, actual=${property.length}`
    )
  }
  return property
}

function float2Half(value: number, floatView: Float32Array, int32View: Int32Array): number {
  floatView[0] = value
  const bits32 = int32View[0]
  let bits16 = (bits32 >> 16) & 0x8000
  let mantissa = (bits32 >> 12) & 0x07ff
  const exponent = (bits32 >> 23) & 0xff
  if (exponent < 103) return bits16
  if (exponent > 142) {
    bits16 |= 0x7c00
    bits16 |= ((exponent === 255 ? 0 : 1) && bits32 & 0x007fffff) as number
    return bits16
  }
  if (exponent < 113) {
    mantissa |= 0x0800
    bits16 |= (mantissa >> (114 - exponent)) + ((mantissa >> (113 - exponent)) & 1)
    return bits16
  }
  bits16 |= ((exponent - 112) << 10) | (mantissa >> 1)
  bits16 += mantissa & 1
  return bits16
}

function packColorData(data: GSplatData, capacity: number): Uint16Array {
  const result = new Uint16Array(capacity * 4)
  const red = requireProp(data, 'f_dc_0')
  const green = requireProp(data, 'f_dc_1')
  const blue = requireProp(data, 'f_dc_2')
  const opacity = requireProp(data, 'opacity')
  const floatView = new Float32Array(1)
  const int32View = new Int32Array(floatView.buffer)
  for (let index = 0; index < data.numSplats; index++) {
    const offset = index * 4
    result[offset] = float2Half(red[index] * SH_C0 + 0.5, floatView, int32View)
    result[offset + 1] = float2Half(green[index] * SH_C0 + 0.5, floatView, int32View)
    result[offset + 2] = float2Half(blue[index] * SH_C0 + 0.5, floatView, int32View)
    const alpha = data.activated ? opacity[index] : 1 / (1 + Math.exp(-opacity[index]))
    result[offset + 3] = float2Half(alpha, floatView, int32View)
  }
  return result
}

function packTransformData(
  data: GSplatData,
  capacity: number
): Pick<PackedUncompressedData, 'transformA' | 'transformB'> {
  requireProp(data, 'x')
  requireProp(data, 'y')
  requireProp(data, 'z')
  requireProp(data, 'rot_0')
  requireProp(data, 'rot_1')
  requireProp(data, 'rot_2')
  requireProp(data, 'rot_3')
  requireProp(data, 'scale_0')
  requireProp(data, 'scale_1')
  requireProp(data, 'scale_2')

  const transformA = new Uint32Array(capacity * 4)
  const transformAFloat = new Float32Array(transformA.buffer)
  const transformB = new Uint16Array(capacity * 4)
  const position = new THREE.Vector3()
  const rotation = new THREE.Quaternion()
  const scale = new THREE.Vector3()
  const iterator = data.createIter(position, rotation, scale)
  const floatView = new Float32Array(1)
  const int32View = new Int32Array(floatView.buffer)

  for (let index = 0; index < data.numSplats; index++) {
    iterator.read(index)
    let { x, y, z, w } = rotation
    const length = Math.sqrt(x * x + y * y + z * z + w * w)
    if (length === 0) {
      x = 0
      y = 0
      z = 0
      w = 1
    } else {
      x /= length
      y /= length
      z /= length
      w /= length
    }
    if (w < 0) {
      x = -x
      y = -y
      z = -z
    }

    const offset = index * 4
    transformAFloat[offset] = position.x
    transformAFloat[offset + 1] = position.y
    transformAFloat[offset + 2] = position.z
    transformA[offset + 3] =
      float2Half(x, floatView, int32View) | (float2Half(y, floatView, int32View) << 16)
    transformB[offset] = float2Half(scale.x, floatView, int32View)
    transformB[offset + 1] = float2Half(scale.y, floatView, int32View)
    transformB[offset + 2] = float2Half(scale.z, floatView, int32View)
    transformB[offset + 3] = float2Half(z, floatView, int32View)
  }
  return { transformA, transformB }
}

function quantizeSH(value: number, max: number, limit: number): number {
  return Math.max(0, Math.min(limit, Math.floor(((value / max) * 0.5 + 0.5) * limit + 0.5)))
}

function pack111011(red: number, green: number, blue: number): number {
  return ((red << 21) | (green << 11) | blue) >>> 0
}

function packSHData(
  data: GSplatData,
  capacity: number,
  shBands: number
): Pick<
  PackedUncompressedData,
  'splatSH_1to3' | 'splatSH_4to7' | 'splatSH_8to11' | 'splatSH_12to15'
> {
  if (shBands === 0) return {}
  const numCoefficients = SH_COEFFICIENTS[shBands]
  const source = Array.from({ length: numCoefficients * 3 }, (_, index) =>
    requireProp(data, `f_rest_${index}`)
  )
  const splatSH_1to3 = new Uint32Array(capacity * 4)
  const splatSH_4to7 = shBands > 1 ? new Uint32Array(capacity * 4) : undefined
  const splatSH_8to11 = shBands > 1 ? new Uint32Array(capacity * (shBands > 2 ? 4 : 1)) : undefined
  const splatSH_12to15 = shBands > 2 ? new Uint32Array(capacity * 4) : undefined
  const coefficients = new Array<number>(numCoefficients * 3).fill(0)
  const maxFloat = new Float32Array(1)
  const maxUint = new Uint32Array(maxFloat.buffer)
  const limit11 = (1 << 11) - 1
  const limit10 = (1 << 10) - 1

  for (let index = 0; index < data.numSplats; index++) {
    for (let coefficient = 0; coefficient < numCoefficients; coefficient++) {
      coefficients[coefficient * 3] = source[coefficient][index]
      coefficients[coefficient * 3 + 1] = source[coefficient + numCoefficients][index]
      coefficients[coefficient * 3 + 2] = source[coefficient + numCoefficients * 2][index]
    }

    // 冻结上游故意以 signed c[0] 初始化，不能改成 abs(c[0])。
    let max = coefficients[0]
    for (let coefficient = 1; coefficient < numCoefficients * 3; coefficient++) {
      max = Math.max(max, Math.abs(coefficients[coefficient]))
    }
    if (max === 0) continue

    for (let coefficient = 0; coefficient < numCoefficients; coefficient++) {
      const offset = coefficient * 3
      coefficients[offset] = quantizeSH(coefficients[offset], max, limit11)
      coefficients[offset + 1] = quantizeSH(coefficients[offset + 1], max, limit10)
      coefficients[offset + 2] = quantizeSH(coefficients[offset + 2], max, limit11)
    }

    maxFloat[0] = max
    const offset = index * 4
    splatSH_1to3[offset] = maxUint[0]
    splatSH_1to3[offset + 1] = pack111011(coefficients[0], coefficients[1], coefficients[2])
    splatSH_1to3[offset + 2] = pack111011(coefficients[3], coefficients[4], coefficients[5])
    splatSH_1to3[offset + 3] = pack111011(coefficients[6], coefficients[7], coefficients[8])

    if (shBands > 1) {
      splatSH_4to7![offset] = pack111011(coefficients[9], coefficients[10], coefficients[11])
      splatSH_4to7![offset + 1] = pack111011(coefficients[12], coefficients[13], coefficients[14])
      splatSH_4to7![offset + 2] = pack111011(coefficients[15], coefficients[16], coefficients[17])
      splatSH_4to7![offset + 3] = pack111011(coefficients[18], coefficients[19], coefficients[20])

      if (shBands > 2) {
        for (let target = 0; target < 4; target++) {
          const coefficient = 21 + target * 3
          splatSH_8to11![offset + target] = pack111011(
            coefficients[coefficient],
            coefficients[coefficient + 1],
            coefficients[coefficient + 2]
          )
        }
        for (let target = 0; target < 4; target++) {
          const coefficient = 33 + target * 3
          splatSH_12to15![offset + target] = pack111011(
            coefficients[coefficient],
            coefficients[coefficient + 1],
            coefficients[coefficient + 2]
          )
        }
      } else {
        splatSH_8to11![index] = pack111011(coefficients[21], coefficients[22], coefficients[23])
      }
    }
  }
  return { splatSH_1to3, splatSH_4to7, splatSH_8to11, splatSH_12to15 }
}

function packData(data: GSplatData, capacity: number): PackedUncompressedData {
  const splatColor = packColorData(data, capacity)
  const transforms = packTransformData(data, capacity)
  const sh = packSHData(data, capacity, data.shBands)
  return { splatColor, ...transforms, ...sh }
}

function createShapeReader(data: GSplatData): SplatShapeReader {
  const position = new THREE.Vector3()
  const rotation = new THREE.Quaternion()
  const scale = new THREE.Vector3()
  const iterator = data.createIter(position, rotation, scale)
  return (index, targetPosition, targetRotation, targetRadii) => {
    if (!Number.isSafeInteger(index) || index < 0 || index >= data.numSplats) return false
    iterator.read(index)
    if (
      !Number.isFinite(position.x) ||
      !Number.isFinite(position.y) ||
      !Number.isFinite(position.z) ||
      !Number.isFinite(scale.x) ||
      !Number.isFinite(scale.y) ||
      !Number.isFinite(scale.z) ||
      scale.x <= 0 ||
      scale.y <= 0 ||
      scale.z <= 0
    ) {
      return false
    }
    targetPosition.copy(position)
    targetRotation.copy(rotation).normalize()
    if (targetRotation.w < 0) {
      targetRotation.set(-targetRotation.x, -targetRotation.y, -targetRotation.z, -targetRotation.w)
    }
    targetRadii.copy(scale)
    return true
  }
}

function createColorReader(data: GSplatData): SplatColorReader {
  const color = new THREE.Vector4()
  const iterator = data.createIter(undefined, undefined, undefined, color)
  return (index, targetColor) => {
    if (!Number.isSafeInteger(index) || index < 0 || index >= data.numSplats) return false
    iterator.read(index)
    if (!Number.isFinite(color.x) || !Number.isFinite(color.y) || !Number.isFinite(color.z)) {
      return false
    }
    targetColor.copy(color)
    return true
  }
}

/** 普通 PLY/.splat 的格式原生 Unified Resource。 */
export class GSplatResource<
  TData extends GSplatResourceDataSource = GSplatData,
  TIterator = unknown,
> extends GSplatResourceBase<TData, TIterator> {
  readonly kind = 'uncompressed' as const
  readonly shBands: number

  constructor(options: GSplatResourceBaseOptions<TData, TIterator>) {
    super(options)
    const shBands = (options.data as { readonly shBands?: unknown }).shBands ?? 0
    if (!Number.isSafeInteger(shBands) || Number(shBands) < 0 || Number(shBands) > 3) {
      throw new Error(`GSplatResource: 非法 shBands=${String(shBands)}`)
    }
    this.shBands = Number(shBands)
  }

  /**
   * 先完成全部 CPU pack，再事务创建/上传 GPU leaves，成功后才发布 Resource。
   * WebGL2 与 WebGPU 只替换 adapter，不分叉 Resource 对象模型。
   */
  static create(
    adapter: GSplatGraphicsAdapter,
    data: GSplatData,
    options: GSplatResourceCreateOptions = {}
  ): GSplatResource<GSplatData> {
    const format = createFormat(adapter, data.shBands)
    const dimensions = adapter.calculateTextureDimensions(data.numSplats)
    const capacity = dimensions.width * dimensions.height
    if (!Number.isSafeInteger(capacity) || capacity < data.numSplats) {
      throw new Error(
        `GSplatResource: texture capacity 不足，capacity=${capacity}, numSplats=${data.numSplats}`
      )
    }
    const packed = packData(data, capacity)
    const streams = new GSplatStreams(adapter)
    try {
      streams.init(format, data.numSplats)
      for (const stream of format.resourceStreams) {
        const texture = streams.getTexture(stream.name)
        const source = packed[stream.name as keyof PackedUncompressedData]
        if (!texture || !source)
          throw new Error(`GSplatResource: texture staging 缺失 ${stream.name}`)
        adapter.updateTexture(texture, source)
      }
      const resource = new GSplatResource<GSplatData>({
        ...options,
        adapter,
        data,
        format,
        readSplatShape: options.readSplatShape ?? createShapeReader(data),
        readSplatColor: options.readSplatColor ?? createColorReader(data),
      })
      resource.attachStreams(streams)
      return resource
    } catch (error) {
      streams.destroy()
      throw error
    }
  }

  override configureMaterialDefines(defines: Map<string, unknown>): void {
    super.configureMaterialDefines(defines)
    defines.set('SH_BANDS', this.shBands)
  }
}
